import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMemo, useState, type JSX, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Download, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { apiRequest, ApiError } from '../lib/api';
import { useToast } from '../lib/toast';
import { useAuthStore } from '../lib/auth-store';
import { useConfirm } from '../components/ui/Confirm';
import { formatDate, timeAgo } from '../lib/format';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { GatedSection } from '../components/shared/GatedSection';
import { DetailHeader } from '../components/shared/DetailHeader';
import { ActivityPanel } from '../components/shared/ActivityPanel';
import { ResourceEditDrawer } from '../components/academics/ResourceEditDrawer';
import {
  humanBytes,
  type BatchRecord,
  type CourseSummary,
  type ResourceRecord,
} from '../components/academics/types';

const API_ORIGIN =
  (import.meta.env.VITE_API_ORIGIN as string | undefined) ??
  'https://api.babulilmonline.com';

const MANAGE_ROLES: ReadonlyArray<string> = [
  'super_admin',
  'academic_admin',
  'nalim',
  'teacher',
];

// The backend agent is enriching GET /api/resources/:id with an embedded
// uploader summary. Treat as optional so the page works before/after rollout.
interface UploaderSummary {
  id: string;
  displayName: string | null;
  email: string | null;
}

interface ResourceDetail extends ResourceRecord {
  uploader?: UploaderSummary | null;
}

export const Route = createFileRoute('/_authed/academics/resources/$resourceId')({
  component: ResourceDetailPage,
});

function ResourceDetailPage(): JSX.Element {
  const { resourceId } = Route.useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const role = useAuthStore((s) => s.user?.primaryRole ?? null);
  const canEdit = role !== null && MANAGE_ROLES.includes(role);
  // The plan calls out tightening delete to super_admin; the existing endpoint
  // still allows the wider WRITE set so we client-gate to super_admin per spec
  // and rely on the server to enforce its own rule on top.
  const canDelete = role === 'super_admin';

  const [editOpen, setEditOpen] = useState(false);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const resourceQuery = useQuery({
    queryKey: ['resource', 'detail', resourceId] as const,
    queryFn: ({ signal }) =>
      apiRequest<ResourceDetail>(
        `/api/resources/${encodeURIComponent(resourceId)}`,
        { signal },
      ),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && (err.status === 404 || err.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const resource = resourceQuery.data;

  // Lookup queries serve both the Linked-to fallback (when uploader/scope
  // names aren't embedded) and feed the edit drawer's selects.
  const batchesQuery = useQuery({
    queryKey: ['batches', 'resources-lookup'] as const,
    queryFn: ({ signal }) =>
      apiRequest<{ items: BatchRecord[] }>('/api/batches', {
        signal,
        query: { page: 1, pageSize: 100 },
      }),
    staleTime: 60_000,
  });

  const coursesQuery = useQuery({
    queryKey: ['courses', 'resources-lookup'] as const,
    queryFn: ({ signal }) =>
      apiRequest<{ items: CourseSummary[] }>('/api/courses', { signal }),
    staleTime: 60_000,
  });

  // Mint the signed URL once the resource is known. The path is short-lived
  // (5 min) so the inline preview reuses the same URL the Download button
  // would open. The endpoint re-authorizes on every hit.
  const downloadUrlQuery = useQuery({
    queryKey: ['resource', 'download-url', resourceId] as const,
    enabled: !!resource,
    queryFn: ({ signal }) =>
      apiRequest<{ url: string; expiresInSeconds: number }>(
        `/api/resources/${encodeURIComponent(resourceId)}/download-url`,
        { signal },
      ),
    // Tie staleness to the server-declared TTL minus a small margin so we
    // refetch slightly before the URL would actually expire.
    staleTime: 4 * 60_000,
  });

  const previewUrl = downloadUrlQuery.data
    ? `${API_ORIGIN}${downloadUrlQuery.data.url}`
    : null;

  const batchSummary = useMemo(() => {
    if (!resource?.batchId) return null;
    const found = batchesQuery.data?.items.find((b) => b.id === resource.batchId);
    return found ? { id: found.id, name: found.name } : { id: resource.batchId, name: resource.batchId.slice(0, 8) };
  }, [resource, batchesQuery.data]);

  const courseSummary = useMemo(() => {
    if (!resource?.courseId) return null;
    const found = coursesQuery.data?.items.find((c) => c.id === resource.courseId);
    return found ? { id: found.id, name: found.name } : { id: resource.courseId, name: resource.courseId.slice(0, 8) };
  }, [resource, coursesQuery.data]);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['resource', 'detail', resourceId] });
    void queryClient.invalidateQueries({ queryKey: ['resources'] });
  };

  const deleteMutation = useMutation({
    mutationFn: () =>
      apiRequest<{ ok: true }>(`/api/resources/${encodeURIComponent(resourceId)}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      toast('Resource deleted.', { variant: 'success' });
      void queryClient.invalidateQueries({ queryKey: ['resources'] });
      void navigate({ to: '/academics/resources' });
    },
    onError: (err: unknown) => {
      toast(err instanceof ApiError ? err.message : 'Could not delete resource.', {
        variant: 'error',
      });
    },
  });

  const handleDelete = async (): Promise<void> => {
    if (!resource) return;
    const ok = await confirm({
      title: 'Delete resource?',
      description: `"${resource.title}" will be removed for everyone with access.`,
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (!ok) return;
    setOverflowOpen(false);
    deleteMutation.mutate();
  };

  const handleDownload = async (): Promise<void> => {
    if (downloading) return;
    setDownloading(true);
    try {
      // Prefer a fresh URL rather than reusing the cached preview token so the
      // download stays reliable even if the cache entry is stale.
      const res = await apiRequest<{ url: string; expiresInSeconds: number }>(
        `/api/resources/${encodeURIComponent(resourceId)}/download-url`,
      );
      window.open(`${API_ORIGIN}${res.url}`, '_blank', 'noopener');
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not start download.', {
        variant: 'error',
      });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl mx-auto w-full">
      <GatedSection
        error={resourceQuery.error}
        title="Resource"
        onRetry={() => resourceQuery.refetch()}
      >
        {resourceQuery.isLoading || !resource ? (
          <div className="space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-96 w-full" />
          </div>
        ) : (
          <div className="space-y-6">
            <DetailHeader
              title={resource.title}
              sublabel={
                <>
                  <span className="font-mono text-[11px]">
                    {filenameFromKey(resource.r2Key)}
                  </span>
                  <span aria-hidden="true">·</span>
                  <span>{humanBytes(resource.sizeBytes)}</span>
                  <span aria-hidden="true">·</span>
                  <span>Uploaded {timeAgo(resource.createdAt)}</span>
                </>
              }
              statusChip={
                <ScopeChip
                  batchId={resource.batchId}
                  courseId={resource.courseId}
                />
              }
              backTo="/academics/resources"
              actions={
                <>
                  {canEdit ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<Pencil size={14} aria-hidden="true" />}
                      onClick={() => setEditOpen(true)}
                    >
                      Edit
                    </Button>
                  ) : null}
                  <Button
                    size="sm"
                    icon={<Download size={14} aria-hidden="true" />}
                    loading={downloading}
                    onClick={() => {
                      void handleDownload();
                    }}
                  >
                    Download
                  </Button>
                  {canDelete ? (
                    <OverflowMenu
                      open={overflowOpen}
                      onOpenChange={setOverflowOpen}
                      pending={deleteMutation.isPending}
                      onDelete={() => {
                        void handleDelete();
                      }}
                    />
                  ) : null}
                </>
              }
            />

            <Card title="Preview">
              <ResourcePreview
                mimeType={resource.mimeType}
                title={resource.title}
                previewUrl={previewUrl}
                isLoading={downloadUrlQuery.isLoading && !downloadUrlQuery.data}
                error={
                  downloadUrlQuery.isError
                    ? downloadUrlQuery.error instanceof ApiError
                      ? downloadUrlQuery.error.message
                      : 'Could not load preview.'
                    : null
                }
              />
            </Card>

            <section className="grid gap-4 lg:grid-cols-2">
              <Card title="Details">
                <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
                  <DetailRow label="Title" value={resource.title} />
                  <DetailRow
                    label="Description"
                    value={
                      resource.description ? (
                        <span className="whitespace-pre-wrap">
                          {resource.description}
                        </span>
                      ) : (
                        <span className="text-[color:var(--color-text-muted)]">—</span>
                      )
                    }
                  />
                  <DetailRow
                    label="MIME type"
                    value={<code className="font-mono text-xs">{resource.mimeType}</code>}
                  />
                  <DetailRow label="Size" value={humanBytes(resource.sizeBytes)} />
                  <DetailRow
                    label="R2 key"
                    value={
                      <code className="font-mono text-[11px] text-[color:var(--color-text-muted)]">
                        {resource.r2Key}
                      </code>
                    }
                  />
                  <DetailRow
                    label="Uploaded"
                    value={timeAgo(resource.createdAt)}
                  />
                  {resource.updatedAt && resource.updatedAt !== resource.createdAt ? (
                    <DetailRow label="Updated" value={timeAgo(resource.updatedAt)} />
                  ) : null}
                </dl>
              </Card>

              <Card title="Linked to">
                {batchSummary ? (
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
                        Batch
                      </p>
                      <p className="mt-1 text-sm font-medium text-[color:var(--color-text)]">
                        {batchSummary.name}
                      </p>
                    </div>
                    <Link
                      to="/academics/batches/$batchId"
                      params={{ batchId: batchSummary.id }}
                      className="text-xs font-medium text-gold-700 hover:underline"
                    >
                      Open batch
                    </Link>
                  </div>
                ) : courseSummary ? (
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
                        Course
                      </p>
                      <p className="mt-1 text-sm font-medium text-[color:var(--color-text)]">
                        {courseSummary.name}
                      </p>
                    </div>
                    <Link
                      to="/academics/courses/$courseId"
                      params={{ courseId: courseSummary.id }}
                      className="text-xs font-medium text-gold-700 hover:underline"
                    >
                      Open course
                    </Link>
                  </div>
                ) : (
                  <p className="text-sm text-[color:var(--color-text-muted)]">
                    Not linked to a batch or course.
                  </p>
                )}
              </Card>

              <Card title="Uploader">
                {resource.uploader ? (
                  <div>
                    <p className="text-sm font-medium text-[color:var(--color-text)]">
                      {resource.uploader.displayName ?? 'Unknown'}
                    </p>
                    {resource.uploader.email ? (
                      <p className="mt-0.5 text-xs text-[color:var(--color-text-muted)]">
                        {resource.uploader.email}
                      </p>
                    ) : null}
                    <p className="mt-2 text-xs text-[color:var(--color-text-muted)]">
                      Uploaded {formatDate(resource.createdAt, { withTime: true })}
                    </p>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm text-[color:var(--color-text-muted)]">
                      Uploaded by user{' '}
                      <code className="font-mono text-[11px]">
                        {resource.uploadedBy.slice(0, 8)}
                      </code>
                    </p>
                    <p className="mt-2 text-xs text-[color:var(--color-text-muted)]">
                      {formatDate(resource.createdAt, { withTime: true })}
                    </p>
                  </div>
                )}
              </Card>
            </section>

            <ActivityPanel
              targetType="resource"
              targetId={resource.id}
              title="Activity"
            />
          </div>
        )}
      </GatedSection>

      {canEdit ? (
        <ResourceEditDrawer
          open={editOpen}
          onClose={() => setEditOpen(false)}
          resource={resource ?? null}
          batches={batchesQuery.data?.items ?? []}
          courses={coursesQuery.data?.items ?? []}
          onSaved={() => {
            setEditOpen(false);
            invalidate();
          }}
        />
      ) : null}
    </div>
  );
}

function ResourcePreview({
  mimeType,
  title,
  previewUrl,
  isLoading,
  error,
}: {
  mimeType: string;
  title: string;
  previewUrl: string | null;
  isLoading: boolean;
  error: string | null;
}): JSX.Element {
  const isImage = mimeType.startsWith('image/');
  const isPdf = mimeType === 'application/pdf';
  const isPreviewable = isImage || isPdf;

  if (!isPreviewable) {
    return (
      <p className="text-sm text-[color:var(--color-text-muted)]">
        Preview not available for this file type — Download to view.
      </p>
    );
  }

  if (isLoading) {
    return <Skeleton className="h-72 w-full" />;
  }

  if (error || !previewUrl) {
    return (
      <p className="text-sm text-[color:var(--color-danger)]">
        {error ?? 'Preview unavailable.'}
      </p>
    );
  }

  if (isImage) {
    return (
      <div className="overflow-hidden rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface)]">
        <img
          src={previewUrl}
          alt={title}
          className="mx-auto block max-h-[70vh] w-auto max-w-full"
        />
      </div>
    );
  }

  // PDF
  return (
    <div className="overflow-hidden rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface)]">
      <iframe
        src={previewUrl}
        title={title}
        className="block h-[70vh] w-full"
      />
    </div>
  );
}

function ScopeChip({
  batchId,
  courseId,
}: {
  batchId: string | null;
  courseId: string | null;
}): JSX.Element {
  let label = 'Unlinked';
  let tone =
    'bg-[color:var(--color-surface)] text-[color:var(--color-text-muted)]';
  if (batchId) {
    label = 'Batch';
    tone = 'bg-gold-50 text-gold-700 dark:bg-gold-700/20';
  } else if (courseId) {
    label = 'Course';
    tone = 'bg-[color:var(--color-success)]/15 text-[color:var(--color-success)]';
  }
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold',
        tone,
      )}
    >
      {label}
    </span>
  );
}

function Card({
  title,
  children,
  className,
}: {
  title: string;
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <div
      className={clsx(
        'rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5',
        className,
      )}
    >
      <h3 className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
        {title}
      </h3>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function DetailRow({
  label,
  value,
}: {
  label: string;
  value: ReactNode;
}): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-[color:var(--color-border)] pb-2 last:border-b-0 last:pb-0">
      <dt className="text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
        {label}
      </dt>
      <dd className="min-w-0 truncate text-sm text-[color:var(--color-text)]">
        {value}
      </dd>
    </div>
  );
}

const filenameFromKey = (key: string): string => {
  const tail = key.split('/').pop() ?? 'resource';
  return tail.length > 60 ? `${tail.slice(0, 57)}…` : tail;
};

interface OverflowMenuProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  pending: boolean;
  onDelete: () => void;
}

function OverflowMenu({
  open,
  onOpenChange,
  pending,
  onDelete,
}: OverflowMenuProps): JSX.Element {
  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="sm"
        icon={<MoreHorizontal size={14} aria-hidden="true" />}
        onClick={() => onOpenChange(!open)}
        aria-haspopup="menu"
        aria-expanded={open}
        loading={pending}
      >
        More
      </Button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 z-10 mt-1 min-w-[160px] rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] py-1 shadow-lg"
        >
          <button
            role="menuitem"
            type="button"
            onClick={onDelete}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[color:var(--color-danger)] hover:bg-[color:var(--color-danger)]/10 focus:outline-none"
          >
            <Trash2 size={14} aria-hidden="true" />
            Delete
          </button>
        </div>
      ) : null}
    </div>
  );
}
